import React from 'react';
import SignupLink from '@/components/SignupLink';
import { ArrowRight, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import brandLogo from '@/assets/images/menu-bar-logo.svg';
import { getPublicPlatformBillingCatalog } from '@/lib/api/platformBilling';
import type { PlatformPlan, PublicPlatformBillingCatalog } from '@/types/platformBilling';
import styles from './PricingSection.module.css';
import { CreditPolicy } from '@/components/billing/CreditPolicy';
import { CohortCreditGuide } from '@/components/billing/CohortCreditGuide';
import { formatBillingMoney } from '@/lib/billingCatalog';

type BillingPeriod = PlatformPlan['billingPeriod'];

const BILLING_PERIODS: Array<{ value: BillingPeriod; label: string }> = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'half_yearly', label: 'Every 6 months' },
  { value: 'yearly', label: 'Yearly' },
];

const PERIOD_MONTHS: Record<BillingPeriod, number> = {
  monthly: 1,
  half_yearly: 6,
  yearly: 12,
};

const money = formatBillingMoney;

const TAX_NOTE = 'Prices exclude tax. Your bank may charge in your local currency at checkout.';

const planFor = (
  catalog: PublicPlatformBillingCatalog,
  tier: PlatformPlan['tier'],
  period: BillingPeriod,
) => catalog.plans.find((plan) => plan.tier === tier && plan.billingPeriod === period);

const billedLabel = (plan: PlatformPlan, currency: string) => {
  const total = money(plan.amountUsd, currency);
  if (plan.billingPeriod === 'monthly') return `${total} billed monthly`;
  if (plan.billingPeriod === 'half_yearly') return `${total} billed every 6 months`;
  return `${total} billed annually`;
};

const trialDurationLabel = (minutes: number | undefined) => {
  if (!minutes) return '30-day free trial';
  if (minutes < 60) return `${minutes}-minute free trial`;
  if (minutes % (24 * 60) === 0) return `${minutes / (24 * 60)}-day free trial`;
  if (minutes % 60 === 0) return `${minutes / 60}-hour free trial`;
  return `${minutes}-minute free trial`;
};

const PricingSection = () => {
  const [loadedCatalog, setLoadedCatalog] = React.useState<PublicPlatformBillingCatalog | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [billingPeriod, setBillingPeriod] = React.useState<BillingPeriod>('yearly');
  const catalog = loadedCatalog;

  const load = React.useCallback(async () => {
    setError(null);
    try {
      const nextCatalog = await getPublicPlatformBillingCatalog();
      if (nextCatalog.source !== 'stripe') throw new Error('Unexpected pricing source');
      setLoadedCatalog(nextCatalog);
    } catch {
      setLoadedCatalog(null);
      setError('Current plans could not be loaded from Stripe. Please try again or start signup to continue.');
    }
  }, []);

  React.useEffect(() => { void load(); }, [load]);

  const displayedPlans = React.useMemo(() => {
    if (!catalog) return [];
    return [
      planFor(catalog, 'basic', billingPeriod),
      planFor(catalog, 'professional', billingPeriod),
    ].filter((plan): plan is PlatformPlan => Boolean(plan));
  }, [billingPeriod, catalog]);
  // Radio-group keyboard pattern: arrows move and select, Home/End jump to the ends.
  const handlePeriodKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const currentIndex = BILLING_PERIODS.findIndex((period) => period.value === billingPeriod);
    const lastIndex = BILLING_PERIODS.length - 1;
    const nextIndex = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? (currentIndex + 1) % BILLING_PERIODS.length
      : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? (currentIndex + lastIndex) % BILLING_PERIODS.length
        : event.key === 'Home' ? 0
          : event.key === 'End' ? lastIndex
            : -1;
    if (nextIndex < 0) return;
    event.preventDefault();
    const nextPeriod = BILLING_PERIODS[nextIndex].value;
    setBillingPeriod(nextPeriod);
    event.currentTarget.querySelector<HTMLButtonElement>(`[data-billing-period="${nextPeriod}"]`)?.focus();
  };

  return (
    <section id="pricing" className={styles.section}>
      <div className={styles.wrapper}>
        <div className={styles.heading}>
          <p className={styles.eyebrow}>Pricing</p>
          <h2 className={styles.title}>Choose the plan that fits your practice</h2>
          <p className={styles.trialMessage}>New customers get a {trialDurationLabel(catalog?.trialMinutes)} with every plan.</p>
        </div>

        {!catalog && !error ? (
          <div className={styles.statusCard} role="status">
            <Loader2 size={18} className={styles.spinner} /> Loading current plans from Stripe…
          </div>
        ) : null}

        {error ? (
          <div className={styles.statusCard} role="alert">
            <p>{error}</p>
            <div className={styles.statusActions}>
              <Button type="button" variant="outline" onClick={() => void load()}>Try again</Button>
              <Button asChild><SignupLink>Get started</SignupLink></Button>
            </div>
          </div>
        ) : null}

        {catalog && displayedPlans.length === 2 ? (
          <div
            className={styles.pricingTable}
            data-pricing-source="stripe"
            data-pricing-mode="display-only"
          >
            <div
              className={styles.periodSelector}
              role="radiogroup"
              aria-label="Billing period"
              onKeyDown={handlePeriodKeyDown}
            >
              {BILLING_PERIODS.map((period) => (
                <button
                  key={period.value}
                  type="button"
                  role="radio"
                  aria-checked={billingPeriod === period.value}
                  tabIndex={billingPeriod === period.value ? 0 : -1}
                  data-billing-period={period.value}
                  className={`${styles.periodButton} ${billingPeriod === period.value ? styles.periodButtonActive : ''}`}
                  onClick={() => setBillingPeriod(period.value)}
                >
                  {period.label}
                </button>
              ))}
            </div>

            <div className={styles.planColumns}>
              {displayedPlans.map((plan) => {
                const monthlyAmount = plan.amountUsd / PERIOD_MONTHS[plan.billingPeriod];
                const planFacts = [
                  `Up to ${plan.clientLimit.toLocaleString()} clients`,
                  `${plan.includedCredits.toLocaleString()} AI credits per month`,
                  `${plan.loginLimit} practitioner login`,
                  // Client-capacity add-ons are retired; only advertise them where the plan still allows them.
                  ...(plan.featureAccess?.client_capacity_addons === true && plan.additionalClientBlockSize > 0
                    ? [`Add ${plan.additionalClientBlockSize} clients for ${money(plan.additionalClientBlockAmountUsdMonthly, catalog.currency)}/month`]
                    : []),
                ];
                const features = [...plan.features]
                  .filter((feature) => !feature.disclosure && feature.availability !== 'coming_soon')
                  .sort((left, right) => (right.importance ?? 0) - (left.importance ?? 0));

                return (
                  <article
                    key={plan.code}
                    className={styles.planColumn}
                    data-testid={`pricing-plan-${plan.code}`}
                  >
                    <img src={brandLogo} alt="CalorieScience" className={styles.brandLogo} />
                    <h3 className={styles.planName}>{plan.name}</h3>
                    <div className={styles.priceLine}>
                      <span className={styles.planPrice}>{money(monthlyAmount, catalog.currency)}</span>
                      <span className={styles.planCadence}>per<br />month</span>
                    </div>
                    <p className={styles.billedTotal}>{billedLabel(plan, catalog.currency)}</p>

                    <p className={styles.includesLabel}>This includes:</p>
                    <ul className={styles.featureList}>
                      {planFacts.map((fact) => (
                        <li key={fact} className={styles.featureItem}>
                          <span className={styles.checkIcon} aria-hidden="true"><Check /></span>
                          <span>{fact}</span>
                        </li>
                      ))}
                      {features.map((feature) => (
                        <li key={feature.label} className={styles.featureItem}>
                          <span className={styles.checkIcon} aria-hidden="true"><Check /></span>
                          <span>{feature.label}{feature.detail ? <small className="mt-1 block text-sm font-normal text-slate-600">{feature.detail}</small> : null}</span>
                        </li>
                      ))}
                    </ul>
                  </article>
                );
              })}
            </div>

            <CreditPolicy catalog={catalog} />
            <CohortCreditGuide usage={catalog.creditUsage} />

            <div className={styles.tableFooter}>
              <p>
                Live prices and plan details from Stripe · {catalog.currency.toUpperCase()}. {TAX_NOTE} Choose your plan securely after creating your account.
              </p>
              <Button asChild className={styles.signupButton}>
                <SignupLink
                  data-analytics-event="Landing CTA Clicked"
                  data-analytics-id="pricing_display_get_started"
                  data-analytics-label="Pricing Get Started"
                  data-analytics-section="pricing"
                >
                  Get Started <ArrowRight size={16} aria-hidden="true" />
                </SignupLink>
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default PricingSection;
